import { ChangeDetectorRef, Component, EventEmitter, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DialogButton } from '@snapon/design-system-angular';
import { AppInfoService } from 'core/app-info/app-info.service';
import { NotificationService } from 'core/notification/notification.service';
import { NotificationType } from 'core/notification/notification-config.class';
import { TouchpointsService } from '../services/touchpoints.service';
import { OutlookContact } from '../entities/outlook-contact';
import { TouchpointsAccount } from '../entities/touchpoints-account';

@Component({
  selector: 'app-merge-contact-modal',
  templateUrl: './merge-contact-modal.component.html',
  styleUrls: ['./merge-contact-modal.component.scss'],
  standalone: false
})
export class MergeContactModalComponent implements OnInit {
  appStrings = this.appInfoService.appStrings;
  form: UntypedFormGroup;
  accountOptions: { value: string, label: string, description: string }[] = [];
  selectedAccount: TouchpointsAccount = null;
  loading = false;
  merging = false;
  confirmDialogOpen$ = new EventEmitter<boolean>();
  confirmDialogContent = '';
  confirmDialogButtons: DialogButton[] = [];

  private allAccounts: TouchpointsAccount[] = [];

  get contact(): OutlookContact {
    return this.data.contact;
  }

  get contactFullName(): string {
    return [this.contact.firstName, this.contact.lastName].filter(Boolean).join(' ');
  }

  get lnStatuses(): { [key: number]: string } {
    return this.data.lnStatuses;
  }

  constructor(
    public dialogRef: MatDialogRef<MergeContactModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {
      contact: OutlookContact;
      amEmployeeId: string;
      lnStatuses: { [key: number]: string };
    },
    private fb: UntypedFormBuilder,
    private appInfoService: AppInfoService,
    private touchpointsService: TouchpointsService,
    private notificationService: NotificationService,
    private ref: ChangeDetectorRef,
  ) {
    this.form = this.fb.group({
      accountName: ['', Validators.required]
    });
  }

  ngOnInit() {
    this.form.controls.accountName.valueChanges.subscribe(accountId => {
      this.selectedAccount = this.allAccounts.find(a => a.id === accountId) ?? null;
    });

    this.loadAccounts();
  }

  private loadAccounts() {
    this.loading = true;
    this.touchpointsService.getAccounts(this.data.amEmployeeId, { name: '', address: '', status: undefined })
      .subscribe((accounts: TouchpointsAccount[]) => {
        this.allAccounts = accounts;
        this.accountOptions = accounts.map(a => ({
          value: a.id,
          label: this.formatAccountName(a),
          description: [a.bpNumber ? `BP#: ${a.bpNumber}` : null, a.address].filter(Boolean).join(', ')
        }));
        this.loading = false;
      });
  }

  private formatAccountName(account: TouchpointsAccount): string {
    return account.name1 + (account.name2 ? ` ${account.name2}` : '');
  }

  merge() {
    if (!this.selectedAccount) {
      return;
    }
    const accountName = this.formatAccountName(this.selectedAccount);
    this.confirmDialogContent = this.appStrings.touchpoints_mergeContactConfirmContent
      .replace('{0}', this.contactFullName)
      .replace('{1}', accountName);
    this.confirmDialogButtons = [
      { type: 'secondary', label: this.appStrings.button_cancel, onclick: () => { } },
      {
        type: 'primary', label: this.appStrings.touchpoints_mergeContact, onclick: () => {
          this.executeMerge();
        }
      }
    ];
    this.ref.detectChanges();
    this.confirmDialogOpen$.emit(true);
  }

  private executeMerge() {
    this.merging = true;
    this.touchpointsService.mergeOutlookContact(
      this.contact.id,
      this.selectedAccount.isLead ? null : this.selectedAccount.id,
      this.selectedAccount.isLead ? this.selectedAccount.id : null,
      () => {
        this.notificationService.showNotification(
          this.appStrings.contacts_mergeNotification,
          NotificationType.Success
        );
        this.dialogRef.close({ merged: true });
      }
    );
  }
}
